import { useState } from "react";
import { gql, useQuery, useMutation } from "@apollo/client";
import { useParams, Link } from "react-router-dom";
import { FaArrowLeft, FaPlus, FaTrash, FaEdit, FaGripVertical, FaSave, FaBolt, FaToggleOn, FaToggleOff } from "react-icons/fa";
import {
  CurrencyDollarIcon, TagIcon, CalendarIcon, UserIcon, LinkIcon, CalculatorIcon,
  CheckCircleIcon, StarIcon, PhoneIcon, EnvelopeIcon, MapPinIcon, BuildingOfficeIcon,
  BriefcaseIcon, ChartBarIcon, ClockIcon, DocumentTextIcon, FlagIcon, GlobeAltIcon,
  HashtagIcon, InformationCircleIcon, KeyIcon, LightBulbIcon, PaperClipIcon,
  PencilIcon, ShoppingCartIcon, TruckIcon, WrenchIcon,
} from "@heroicons/react/20/solid";

import Button from "../components/Button";
import Modal from "../components/Modal";
import FormInput from "../components/FormInput";
import FormulaInput from "../components/FormulaInput";
import FormulaHelpModal from "../components/FormulaHelpModal";
import CardLayoutEditor from "../components/CardLayoutEditor";

const GET_BOARD_SETTINGS = gql`
  query GetSalesFunnelBoardSettings($id: ID!) {
    salesFunnelBoard(id: $id) {
      id
      name
      description
      stages {
        id
        name
        color
        position
        computedFields
      }
      computedFields
      cardLayout
      fieldDefinitions {
        id
        label
        type
        icon
        defaultValue
        options
        required
        position
      }
      teams {
        id
        name
      }
    }
    teams(onlyPro: false) {
      id
      name
    }
  }
`;

const UPDATE_BOARD = gql`
  mutation UpdateSalesFunnelBoard($id: ID!, $name: String, $description: String, $teamIds: [ID!]) {
    updateSalesFunnelBoard(id: $id, name: $name, description: $description, teamIds: $teamIds) {
      success
      message
    }
  }
`;

const CREATE_STAGE = gql`
  mutation CreateSalesFunnelStage($boardId: ID!, $name: String!, $color: String) {
    createSalesFunnelStage(boardId: $boardId, name: $name, color: $color) {
      success
      message
      id
    }
  }
`;

const UPDATE_STAGE = gql`
  mutation UpdateSalesFunnelStage($id: ID!, $name: String, $color: String, $computedFields: JSON) {
    updateSalesFunnelStage(id: $id, name: $name, color: $color, computedFields: $computedFields) {
      success
      message
    }
  }
`;

const UPDATE_BOARD_COMPUTED_FIELDS = gql`
  mutation UpdateSalesFunnelBoardComputedFields($boardId: ID!, $computedFields: JSON!) {
    updateSalesFunnelBoardComputedFields(boardId: $boardId, computedFields: $computedFields) {
      success
      message
    }
  }
`;

const UPDATE_CARD_LAYOUT = gql`
  mutation UpdateSalesFunnelBoardCardLayout($boardId: ID!, $cardLayout: JSON!) {
    updateSalesFunnelBoardCardLayout(boardId: $boardId, cardLayout: $cardLayout) {
      success
      message
    }
  }
`;

const DELETE_STAGE = gql`
  mutation DeleteSalesFunnelStage($id: ID!) {
    deleteSalesFunnelStage(id: $id) {
      success
      message
    }
  }
`;

const REORDER_STAGES = gql`
  mutation ReorderSalesFunnelStages($boardId: ID!, $stageIds: [ID!]!) {
    reorderSalesFunnelStages(boardId: $boardId, stageIds: $stageIds) {
      success
      message
    }
  }
`;

const CREATE_FIELD = gql`
  mutation CreateSalesFunnelFieldDefinition(
    $boardId: ID!
    $label: String!
    $type: String!
    $icon: String
    $defaultValue: JSON
    $options: JSON
    $required: Boolean
  ) {
    createSalesFunnelFieldDefinition(
      boardId: $boardId
      label: $label
      type: $type
      icon: $icon
      defaultValue: $defaultValue
      options: $options
      required: $required
    ) {
      success
      message
      id
    }
  }
`;

const UPDATE_FIELD = gql`
  mutation UpdateSalesFunnelFieldDefinition(
    $id: ID!
    $label: String
    $type: String
    $icon: String
    $defaultValue: JSON
    $options: JSON
    $required: Boolean
  ) {
    updateSalesFunnelFieldDefinition(
      id: $id
      label: $label
      type: $type
      icon: $icon
      defaultValue: $defaultValue
      options: $options
      required: $required
    ) {
      success
      message
    }
  }
`;

const DELETE_FIELD = gql`
  mutation DeleteSalesFunnelFieldDefinition($id: ID!) {
    deleteSalesFunnelFieldDefinition(id: $id) {
      success
      message
    }
  }
`;

const GET_AUTOMATIONS = gql`
  query GetSalesFunnelAutomations($boardId: ID!) {
    salesFunnelAutomations(boardId: $boardId) {
      id
      name
      enabled
      allowMultipleTriggers
      relationship
      sourceBoard {
        id
        name
      }
      triggerConditions
      targetBoard {
        id
        name
        stages {
          id
          name
        }
        fieldDefinitions {
          id
          label
          type
        }
      }
      targetStage {
        id
        name
      }
      fieldMappings
      titleFormula
      parentLinkField {
        id
        label
      }
      userMappings
    }
    salesFunnelBoards {
      id
      name
      stages {
        id
        name
      }
      fieldDefinitions {
        id
        label
        type
      }
    }
    users {
      id
      name
      email
    }
  }
`;

const CREATE_AUTOMATION = gql`
  mutation CreateSalesFunnelAutomation(
    $name: String!
    $sourceBoardId: ID!
    $triggerConditions: JSON!
    $targetBoardId: ID!
    $targetStageId: ID!
    $fieldMappings: JSON!
    $titleFormula: String!
    $parentLinkFieldId: ID
    $allowMultipleTriggers: Boolean
    $userMappings: JSON
  ) {
    createSalesFunnelAutomation(
      name: $name
      sourceBoardId: $sourceBoardId
      triggerConditions: $triggerConditions
      targetBoardId: $targetBoardId
      targetStageId: $targetStageId
      fieldMappings: $fieldMappings
      titleFormula: $titleFormula
      parentLinkFieldId: $parentLinkFieldId
      allowMultipleTriggers: $allowMultipleTriggers
      userMappings: $userMappings
    ) {
      success
      message
    }
  }
`;

const UPDATE_AUTOMATION = gql`
  mutation UpdateSalesFunnelAutomation(
    $id: ID!
    $name: String
    $enabled: Boolean
    $triggerConditions: JSON
    $targetBoardId: ID
    $targetStageId: ID
    $fieldMappings: JSON
    $titleFormula: String
    $parentLinkFieldId: ID
    $allowMultipleTriggers: Boolean
    $userMappings: JSON
  ) {
    updateSalesFunnelAutomation(
      id: $id
      name: $name
      enabled: $enabled
      triggerConditions: $triggerConditions
      targetBoardId: $targetBoardId
      targetStageId: $targetStageId
      fieldMappings: $fieldMappings
      titleFormula: $titleFormula
      parentLinkFieldId: $parentLinkFieldId
      allowMultipleTriggers: $allowMultipleTriggers
      userMappings: $userMappings
    ) {
      success
      message
    }
  }
`;

const DELETE_AUTOMATION = gql`
  mutation DeleteSalesFunnelAutomation($id: ID!) {
    deleteSalesFunnelAutomation(id: $id) {
      success
      message
    }
  }
`;

const FIELD_TYPES = [
  { value: "STRING", label: "Text" },
  { value: "NUMBER", label: "Number" },
  { value: "BOOLEAN", label: "Checkbox" },
  { value: "DATE", label: "Date" },
  { value: "SELECT", label: "Single Select" },
  { value: "MULTI_SELECT", label: "Multi Select" },
  { value: "COMPUTED", label: "Computed (Formula)" },
  { value: "CARD", label: "Card Link" },
];

export default function SalesFunnelBoardSettingsPage() {
  const { id } = useParams<{ id: string }>();
  const { data, loading, refetch } = useQuery(GET_BOARD_SETTINGS, {
    variables: { id },
    skip: !id,
  });

  const [updateBoard] = useMutation(UPDATE_BOARD);
  const [createStage] = useMutation(CREATE_STAGE);
  const [updateStage] = useMutation(UPDATE_STAGE);
  const [deleteStage] = useMutation(DELETE_STAGE);
  const [reorderStages] = useMutation(REORDER_STAGES);
  const [createField] = useMutation(CREATE_FIELD);
  const [updateField] = useMutation(UPDATE_FIELD);
  const [deleteField] = useMutation(DELETE_FIELD);
  const [createAutomation] = useMutation(CREATE_AUTOMATION);
  const [updateAutomation] = useMutation(UPDATE_AUTOMATION);
  const [deleteAutomation] = useMutation(DELETE_AUTOMATION);
  const [updateBoardComputedFields] = useMutation(UPDATE_BOARD_COMPUTED_FIELDS);
  const [updateCardLayout] = useMutation(UPDATE_CARD_LAYOUT);

  const { data: automationsData, refetch: refetchAutomations } = useQuery(GET_AUTOMATIONS, {
    variables: { boardId: id },
    skip: !id,
  });

  const [activeTab, setActiveTab] = useState<"general" | "stages" | "fields" | "automations" | "computations" | "cardLayout">("general");

  // Computed fields state
  const [stageComputedFields, setStageComputedFields] = useState<Record<string, any[]>>({});
  const [boardComputedFieldDefs, setBoardComputedFieldDefs] = useState<any[]>([]);

  // Card layout state
  const [cardLayoutConfig, setCardLayoutConfig] = useState<any>(null);

  // General settings state
  const [boardName, setBoardName] = useState("");
  const [boardDescription, setBoardDescription] = useState("");
  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>([]);
  const [generalDirty, setGeneralDirty] = useState(false);

  // Stage modal state
  const [showStageModal, setShowStageModal] = useState(false);
  const [draggedStageId, setDraggedStageId] = useState<string | null>(null);
  const [localStages, setLocalStages] = useState<any[]>([]);
  const [editingStage, setEditingStage] = useState<any>(null);
  const [stageName, setStageName] = useState("");
  const [stageColor, setStageColor] = useState("#3B82F6");

  // Field drag state
  const [draggedFieldId, setDraggedFieldId] = useState<string | null>(null);
  const [localFields, setLocalFields] = useState<any[]>([]);

  // Field modal state
  const [showFieldModal, setShowFieldModal] = useState(false);
  const [editingField, setEditingField] = useState<any>(null);
  const [fieldLabel, setFieldLabel] = useState("");
  const [fieldType, setFieldType] = useState("STRING");
  const [fieldRequired, setFieldRequired] = useState(false);
  const [fieldOptions, setFieldOptions] = useState<{ label: string; value: string }[]>([]);
  const [fieldFormula, setFieldFormula] = useState("");
  const [fieldResultType, setFieldResultType] = useState<"STRING" | "NUMBER">("STRING");
  const [fieldHideFromCard, setFieldHideFromCard] = useState(false);
  const [fieldCurrency, setFieldCurrency] = useState("");
  const [fieldIcon, setFieldIcon] = useState("");
  const [showFormulaHelp, setShowFormulaHelp] = useState(false);

  // Automation modal state
  const [showAutomationModal, setShowAutomationModal] = useState(false);
  const [editingAutomation, setEditingAutomation] = useState<any>(null);
  const [automationName, setAutomationName] = useState("");
  const [automationTargetBoardId, setAutomationTargetBoardId] = useState("");
  const [automationTargetStageId, setAutomationTargetStageId] = useState("");
  const [automationTitleFormula, setAutomationTitleFormula] = useState("${title}");
  const [automationTriggerType, setAutomationTriggerType] = useState<"stage" | "field">("stage");
  const [automationTriggerStageId, setAutomationTriggerStageId] = useState("");
  const [automationAllowMultiple, setAutomationAllowMultiple] = useState(false);
  // Trigger conditions for field-based triggers
  const [automationTriggerConditions, setAutomationTriggerConditions] = useState<
    { fieldId: string; operator: string; value: string }[]
  >([]);
  // Field mappings for the new card
  const [automationFieldMappings, setAutomationFieldMappings] = useState<
    { targetFieldId: string; formula: string }[]
  >([]);
  // Parent link field ID - which CARD field on target board links back to source
  const [automationParentLinkFieldId, setAutomationParentLinkFieldId] = useState("");
  // User mappings for the new card
  const [automationUserMappings, setAutomationUserMappings] = useState<
    { type: "fixed" | "copy_from_source"; userId: string }[]
  >([]);

  const board = data?.salesFunnelBoard;
  const allTeams = data?.teams || [];
  const automations = automationsData?.salesFunnelAutomations || [];
  const allBoards = automationsData?.salesFunnelBoards || [];
  const allUsers = automationsData?.users || [];

  // Initialize form when data loads
  if (board && !generalDirty && boardName === "") {
    setBoardName(board.name);
    setBoardDescription(board.description || "");
    setSelectedTeamIds(board.teams?.map((t: any) => t.id) || []);
    // Initialize computed fields from board data
    const stageMap: Record<string, any[]> = {};
    board.stages?.forEach((s: any) => {
      stageMap[s.id] = s.computedFields || [];
    });
    setStageComputedFields(stageMap);
    setBoardComputedFieldDefs(board.computedFields || []);
    setCardLayoutConfig(board.cardLayout || null);
  }

  const handleSaveStageComputedFields = async (stageId: string, fields: any[]) => {
    try {
      await updateStage({ variables: { id: stageId, computedFields: fields } });
      refetch();
    } catch (e) {
      alert((e as Error).message);
    }
  };

  const handleSaveBoardComputedFields = async () => {
    try {
      await updateBoardComputedFields({ variables: { boardId: id, computedFields: boardComputedFieldDefs } });
      refetch();
      alert("Board computations saved!");
    } catch (e) {
      alert((e as Error).message);
    }
  };

  const handleSaveCardLayout = async () => {
    try {
      await updateCardLayout({ variables: { boardId: id, cardLayout: cardLayoutConfig } });
      refetch();
      alert("Card layout saved!");
    } catch (e) {
      alert((e as Error).message);
    }
  };

  const handleSaveGeneral = async () => {
    try {
      await updateBoard({
        variables: {
          id,
          name: boardName,
          description: boardDescription || null,
          teamIds: selectedTeamIds.length > 0 ? selectedTeamIds : null,
        },
      });
      setGeneralDirty(false);
      refetch();
    } catch (e) {
      alert((e as Error).message);
    }
  };

  const openStageModal = (stage?: any) => {
    if (stage) {
      setEditingStage(stage);
      setStageName(stage.name);
      setStageColor(stage.color || "#3B82F6");
    } else {
      setEditingStage(null);
      setStageName("");
      setStageColor("#3B82F6");
    }
    setShowStageModal(true);
  };

  const handleSaveStage = async () => {
    if (!stageName.trim()) return;

    try {
      if (editingStage) {
        await updateStage({
          variables: { id: editingStage.id, name: stageName, color: stageColor },
        });
      } else {
        await createStage({
          variables: { boardId: id, name: stageName, color: stageColor },
        });
      }
      setShowStageModal(false);
      refetch();
    } catch (e) {
      alert((e as Error).message);
    }
  };

  const handleDeleteStage = async (stageId: string) => {
    if (!window.confirm("Delete this stage? Items in this stage must be moved first.")) return;
    try {
      const result = await deleteStage({ variables: { id: stageId } });
      if (!result.data?.deleteSalesFunnelStage?.success) {
        alert(result.data?.deleteSalesFunnelStage?.message || "Failed to delete stage");
      }
      refetch();
    } catch (e) {
      alert((e as Error).message);
    }
  };

  // Stage drag & drop handlers
  const handleStageDragStart = (e: React.DragEvent, stageId: string) => {
    setDraggedStageId(stageId);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleStageDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleStageDrop = async (e: React.DragEvent, targetStageId: string) => {
    e.preventDefault();
    if (!draggedStageId || draggedStageId === targetStageId) {
      setDraggedStageId(null);
      return;
    }

    const stages = [...(board?.stages || [])];
    const draggedIndex = stages.findIndex((s: any) => s.id === draggedStageId);
    const targetIndex = stages.findIndex((s: any) => s.id === targetStageId);

    if (draggedIndex === -1 || targetIndex === -1) {
      setDraggedStageId(null);
      return;
    }

    // Reorder locally for immediate feedback
    const [draggedStage] = stages.splice(draggedIndex, 1);
    stages.splice(targetIndex, 0, draggedStage);
    setLocalStages(stages);

    // Send to server
    try {
      const stageIds = stages.map((s: any) => s.id);
      await reorderStages({ variables: { boardId: id, stageIds } });
      refetch();
    } catch (err) {
      alert((err as Error).message);
      setLocalStages([]);
    }

    setDraggedStageId(null);
  };

  const handleStageDragEnd = () => {
    setDraggedStageId(null);
  };

  // Use local stages if available (during drag), otherwise use server data
  const displayStages = localStages.length > 0 ? localStages : (board?.stages || []);

  const openFieldModal = (field?: any) => {
    if (field) {
      setEditingField(field);
      setFieldLabel(field.label);
      setFieldType(field.type);
      setFieldRequired(field.required);
      const rawOpts = field.options;
      const selectOpts = Array.isArray(rawOpts) ? rawOpts : (rawOpts?.selectOptions || []);
      setFieldOptions(selectOpts);
      setFieldFormula(field.options?.formula || "");
      setFieldResultType(field.options?.resultType || "STRING");
      setFieldHideFromCard(field.options?.hideFromCard || false);
      setFieldCurrency(field.options?.currency || "");
      setFieldIcon(field.options?.icon || "");
    } else {
      setEditingField(null);
      setFieldLabel("");
      setFieldType("STRING");
      setFieldRequired(false);
      setFieldOptions([]);
      setFieldFormula("");
      setFieldResultType("STRING");
      setFieldHideFromCard(false);
      setFieldCurrency("");
      setFieldIcon("");
    }
    setShowFieldModal(true);
  };

  const handleSaveField = async () => {
    if (!fieldLabel.trim()) return;

    try {
      let options: any = {};
      if (["SELECT", "MULTI_SELECT"].includes(fieldType)) {
        options = fieldOptions;
      } else if (fieldType === "COMPUTED") {
        if (!fieldFormula.trim()) {
          alert("Please enter a formula for the computed field");
          return;
        }
        options = { formula: fieldFormula, resultType: fieldResultType };
      }
      // Merge extra options (works for all types including SELECT arrays via spread below)
      const extraOptions: any = {};
      if (fieldHideFromCard) extraOptions.hideFromCard = true;
      if (fieldCurrency) extraOptions.currency = fieldCurrency;
      if (fieldIcon) extraOptions.icon = fieldIcon;
      if (Array.isArray(options)) {
        // SELECT types: store extra options alongside array in a wrapper — but existing code
        // expects array for select options. Keep array as-is, extras stored on field.defaultValue
        // Actually: keep the extra options separately by wrapping
        options = { selectOptions: options, ...extraOptions };
      } else {
        options = { ...options, ...extraOptions };
      }
      if (Object.keys(options).length === 0) options = null;

      if (editingField) {
        await updateField({
          variables: {
            id: editingField.id,
            label: fieldLabel,
            type: fieldType,
            required: fieldRequired,
            options,
          },
        });
      } else {
        await createField({
          variables: {
            boardId: id,
            label: fieldLabel,
            type: fieldType,
            required: fieldRequired,
            options,
          },
        });
      }
      setShowFieldModal(false);
      refetch();
    } catch (e) {
      alert((e as Error).message);
    }
  };

  const handleDeleteField = async (fieldId: string) => {
    if (!window.confirm("Delete this field? All values for this field will be lost.")) return;
    try {
      await deleteField({ variables: { id: fieldId } });
      refetch();
    } catch (e) {
      alert((e as Error).message);
    }
  };

  const addFieldOption = () => {
    setFieldOptions([...fieldOptions, { label: "", value: "" }]);
  };

  const updateFieldOption = (index: number, key: "label" | "value", val: string) => {
    const newOptions = [...fieldOptions];
    newOptions[index][key] = val;
    if (key === "label" && !newOptions[index].value) {
      newOptions[index].value = val.toLowerCase().replace(/\s+/g, "_");
    }
    setFieldOptions(newOptions);
  };

  const removeFieldOption = (index: number) => {
    setFieldOptions(fieldOptions.filter((_, i) => i !== index));
  };

  // Field drag & drop handlers
  const handleFieldDragStart = (e: React.DragEvent, fieldId: string) => {
    setDraggedFieldId(fieldId);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleFieldDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleFieldDrop = async (e: React.DragEvent, targetFieldId: string) => {
    e.preventDefault();
    if (!draggedFieldId || draggedFieldId === targetFieldId) {
      setDraggedFieldId(null);
      return;
    }
    const fields = [...(board?.fieldDefinitions || [])];
    const fromIdx = fields.findIndex((f: any) => f.id === draggedFieldId);
    const toIdx = fields.findIndex((f: any) => f.id === targetFieldId);
    if (fromIdx === -1 || toIdx === -1) { setDraggedFieldId(null); return; }
    const [moved] = fields.splice(fromIdx, 1);
    fields.splice(toIdx, 0, moved);
    setLocalFields(fields);
    setDraggedFieldId(null);
    try {
      await Promise.all(
        fields.map((f: any, idx: number) =>
          updateField({ variables: { id: f.id, position: idx } })
        )
      );
      refetch();
    } catch (err) {
      alert((err as Error).message);
      setLocalFields([]);
    }
  };

  const handleFieldDragEnd = () => setDraggedFieldId(null);

  const displayFields = localFields.length > 0 ? localFields : (board?.fieldDefinitions || []);

  // Icon registry for the field icon picker
  const FIELD_ICONS: { name: string; Icon: React.ComponentType<any> }[] = [
    { name: "CurrencyDollarIcon", Icon: CurrencyDollarIcon },
    { name: "TagIcon", Icon: TagIcon },
    { name: "CalendarIcon", Icon: CalendarIcon },
    { name: "UserIcon", Icon: UserIcon },
    { name: "LinkIcon", Icon: LinkIcon },
    { name: "CalculatorIcon", Icon: CalculatorIcon },
    { name: "CheckCircleIcon", Icon: CheckCircleIcon },
    { name: "StarIcon", Icon: StarIcon },
    { name: "PhoneIcon", Icon: PhoneIcon },
    { name: "EnvelopeIcon", Icon: EnvelopeIcon },
    { name: "MapPinIcon", Icon: MapPinIcon },
    { name: "BuildingOfficeIcon", Icon: BuildingOfficeIcon },
    { name: "BriefcaseIcon", Icon: BriefcaseIcon },
    { name: "ChartBarIcon", Icon: ChartBarIcon },
    { name: "ClockIcon", Icon: ClockIcon },
    { name: "DocumentTextIcon", Icon: DocumentTextIcon },
    { name: "FlagIcon", Icon: FlagIcon },
    { name: "GlobeAltIcon", Icon: GlobeAltIcon },
    { name: "HashtagIcon", Icon: HashtagIcon },
    { name: "InformationCircleIcon", Icon: InformationCircleIcon },
    { name: "KeyIcon", Icon: KeyIcon },
    { name: "LightBulbIcon", Icon: LightBulbIcon },
    { name: "PaperClipIcon", Icon: PaperClipIcon },
    { name: "PencilIcon", Icon: PencilIcon },
    { name: "ShoppingCartIcon", Icon: ShoppingCartIcon },
    { name: "TruckIcon", Icon: TruckIcon },
    { name: "WrenchIcon", Icon: WrenchIcon },
  ];

  // Automation handlers
  const openAutomationModal = (automation?: any) => {
    if (automation) {
      setEditingAutomation(automation);
      setAutomationName(automation.name);
      setAutomationTargetBoardId(automation.targetBoard?.id || "");
      setAutomationTargetStageId(automation.targetStage?.id || "");
      setAutomationTitleFormula(automation.titleFormula || "${title}");
      setAutomationAllowMultiple(automation.allowMultipleTriggers || false);
      // Parse trigger conditions
      const conditions = automation.triggerConditions;
      if (conditions?.type === "field" && conditions?.fieldId === "stage") {
        setAutomationTriggerType("stage");
        setAutomationTriggerStageId(String(conditions.value));
        setAutomationTriggerConditions([]);
      } else if (conditions?.type === "and" && Array.isArray(conditions?.conditions)) {
        // Check if first condition is stage-based
        const stageCondition = conditions.conditions.find((c: any) => c.fieldId === "stage");
        const fieldConditions = conditions.conditions.filter((c: any) => c.fieldId !== "stage");
        if (stageCondition) {
          setAutomationTriggerType("stage");
          setAutomationTriggerStageId(String(stageCondition.value));
        } else {
          setAutomationTriggerType("field");
          setAutomationTriggerStageId("");
        }
        setAutomationTriggerConditions(
          fieldConditions.map((c: any) => ({
            fieldId: String(c.fieldId),
            operator: c.operator || "equals",
            value: String(c.value ?? ""),
          }))
        );
      } else {
        setAutomationTriggerType("stage");
        setAutomationTriggerStageId("");
        setAutomationTriggerConditions([]);
      }
      // Parse field mappings
      const mappings = automation.fieldMappings || [];
      setAutomationFieldMappings(
        mappings.map((m: any) => ({
          targetFieldId: String(m.targetFieldId),
          formula: m.formula || "",
        }))
      );
      setAutomationParentLinkFieldId(automation.parentLinkField?.id ? String(automation.parentLinkField.id) : "");
      // Parse user mappings
      const umappings = automation.userMappings || [];
      setAutomationUserMappings(
        umappings.map((m: any) => ({
          type: m.type || "fixed",
          userId: m.userId ? String(m.userId) : "",
        }))
      );
    } else {
      setEditingAutomation(null);
      setAutomationName("");
      setAutomationTargetBoardId("");
      setAutomationTargetStageId("");
      setAutomationTitleFormula("${title}");
      setAutomationTriggerType("stage");
      setAutomationTriggerStageId("");
      setAutomationAllowMultiple(false);
      setAutomationTriggerConditions([]);
      setAutomationFieldMappings([]);
      setAutomationParentLinkFieldId("");
      setAutomationUserMappings([]);
    }
    setShowAutomationModal(true);
  };

  const handleSaveAutomation = async () => {
    if (!automationName.trim() || !automationTargetBoardId || !automationTargetStageId) {
      alert("Please fill in all required fields");
      return;
    }

    // Build trigger conditions
    let triggerConditions: any;
    const allConditions: any[] = [];
    
    // Add stage condition if set
    if (automationTriggerStageId) {
      allConditions.push({
        type: "field",
        fieldId: "stage",
        operator: "equals",
        value: parseInt(automationTriggerStageId),
      });
    }
    
    // Add field conditions
    automationTriggerConditions.forEach((cond) => {
      if (cond.fieldId && cond.value) {
        allConditions.push({
          type: "field",
          fieldId: parseInt(cond.fieldId),
          operator: cond.operator,
          value: cond.value,
        });
      }
    });
    
    if (allConditions.length === 0) {
      alert("Please add at least one trigger condition");
      return;
    } else if (allConditions.length === 1) {
      triggerConditions = allConditions[0];
    } else {
      triggerConditions = { type: "and", conditions: allConditions };
    }

    // Build field mappings
    const fieldMappings = automationFieldMappings
      .filter((m) => m.targetFieldId && m.formula)
      .map((m) => ({
        targetFieldId: parseInt(m.targetFieldId),
        formula: m.formula,
      }));

    // Build user mappings
    const userMappings = automationUserMappings
      .filter((m) => m.type === "copy_from_source" || (m.type === "fixed" && m.userId))
      .map((m) => ({
        type: m.type,
        ...(m.type === "fixed" && m.userId ? { userId: parseInt(m.userId) } : {}),
      }));

    try {
      if (editingAutomation) {
        await updateAutomation({
          variables: {
            id: editingAutomation.id,
            name: automationName,
            triggerConditions,
            targetBoardId: automationTargetBoardId,
            targetStageId: automationTargetStageId,
            fieldMappings,
            titleFormula: automationTitleFormula,
            parentLinkFieldId: automationParentLinkFieldId || null,
            allowMultipleTriggers: automationAllowMultiple,
            userMappings,
          },
        });
      } else {
        await createAutomation({
          variables: {
            name: automationName,
            sourceBoardId: id,
            triggerConditions,
            targetBoardId: automationTargetBoardId,
            targetStageId: automationTargetStageId,
            fieldMappings,
            titleFormula: automationTitleFormula,
            parentLinkFieldId: automationParentLinkFieldId || null,
            allowMultipleTriggers: automationAllowMultiple,
            userMappings,
          },
        });
      }
      setShowAutomationModal(false);
      refetchAutomations();
    } catch (e) {
      alert((e as Error).message);
    }
  };

  const handleToggleAutomation = async (automation: any) => {
    try {
      await updateAutomation({
        variables: {
          id: automation.id,
          enabled: !automation.enabled,
        },
      });
      refetchAutomations();
    } catch (e) {
      alert((e as Error).message);
    }
  };

  const handleDeleteAutomation = async (automationId: string) => {
    if (!window.confirm("Delete this automation?")) return;
    try {
      await deleteAutomation({ variables: { id: automationId } });
      refetchAutomations();
    } catch (e) {
      alert((e as Error).message);
    }
  };

  if (loading) {
    return (
      <div className="w-full p-4 pt-6">
        <div className="text-center py-10">Loading settings...</div>
      </div>
    );
  }

  if (!board) {
    return (
      <div className="w-full p-4 pt-6">
        <div className="text-center py-10 text-gray-500">Board not found.</div>
      </div>
    );
  }

  return (
    <div className="w-full p-4 pt-6">
      <div className="flex items-center space-x-4 mb-6">
        <Link to={`/funnel/${id}`} className="text-gray-400 hover:text-gray-600">
          <FaArrowLeft />
        </Link>
        <div>
          <h1 className="text-2xl text-title font-bold">Board Settings</h1>
          <p className="text-sm text-gray-500">{board.name}</p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md">
        <div className="border-b">
          <div className="flex">
            {(["general", "stages", "fields", "automations", "computations", "cardLayout"] as const).map((tab) => (
              <button
                key={tab}
                className={`px-6 py-3 text-sm font-medium ${
                  activeTab === tab
                    ? "border-b-2 border-primary text-primary"
                    : "text-gray-500 hover:text-gray-700"
                }`}
                onClick={() => setActiveTab(tab)}
              >
                {tab === "automations" ? (
                  <span className="flex items-center gap-1">
                    <FaBolt className="text-xs" /> Automations
                  </span>
                ) : tab === "cardLayout" ? (
                  "Card Layout"
                ) : (
                  tab.charAt(0).toUpperCase() + tab.slice(1)
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="p-6">
          {activeTab === "general" && (
            <div className="space-y-6 max-w-xl">
              <FormInput
                label="Board Name"
                value={boardName}
                onChange={(e) => {
                  setBoardName(e.target.value);
                  setGeneralDirty(true);
                }}
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  className="w-full border rounded px-3 py-2"
                  rows={3}
                  value={boardDescription}
                  onChange={(e) => {
                    setBoardDescription(e.target.value);
                    setGeneralDirty(true);
                  }}
                  placeholder="Optional description..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Team Access
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  Leave empty to make the board visible to everyone in the company.
                </p>
                <div className="space-y-2 max-h-48 overflow-y-auto border rounded p-2">
                  {allTeams.map((team: any) => (
                    <label key={team.id} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedTeamIds.includes(team.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedTeamIds([...selectedTeamIds, team.id]);
                          } else {
                            setSelectedTeamIds(selectedTeamIds.filter((id) => id !== team.id));
                          }
                          setGeneralDirty(true);
                        }}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">{team.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="pt-4">
                <Button onClick={handleSaveGeneral} disabled={!generalDirty}>
                  <FaSave className="inline mr-2" />
                  Save Changes
                </Button>
              </div>
            </div>
          )}

          {activeTab === "stages" && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <p className="text-sm text-gray-500">
                  Define the stages (columns) for your board.
                </p>
                <Button onClick={() => openStageModal()}>
                  <FaPlus className="inline mr-2" />
                  Add Stage
                </Button>
              </div>

              <div className="space-y-2">
                {displayStages.map((stage: any) => (
                  <div
                    key={stage.id}
                    draggable
                    onDragStart={(e) => handleStageDragStart(e, stage.id)}
                    onDragOver={handleStageDragOver}
                    onDrop={(e) => handleStageDrop(e, stage.id)}
                    onDragEnd={handleStageDragEnd}
                    className={`flex items-center space-x-3 p-3 bg-gray-50 rounded-lg group transition-opacity ${
                      draggedStageId === stage.id ? "opacity-50" : ""
                    }`}
                  >
                    <FaGripVertical className="text-gray-300 cursor-grab" />
                    <div
                      className="w-4 h-4 rounded"
                      style={{ backgroundColor: stage.color || "#E5E7EB" }}
                    />
                    <span className="flex-1 font-medium">{stage.name}</span>
                    <button
                      onClick={() => openStageModal(stage)}
                      className="text-gray-400 hover:text-gray-600 opacity-0 group-hover:opacity-100"
                    >
                      <FaEdit />
                    </button>
                    <button
                      onClick={() => handleDeleteStage(stage.id)}
                      className="text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100"
                    >
                      <FaTrash />
                    </button>
                  </div>
                ))}

                {displayStages.length === 0 && (
                  <p className="text-center text-gray-400 py-8">
                    No stages yet. Add your first stage to get started.
                  </p>
                )}
              </div>
            </div>
          )}

          {activeTab === "fields" && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <p className="text-sm text-gray-500">
                  Define custom fields for items in this board.
                </p>
                <Button onClick={() => openFieldModal()}>
                  <FaPlus className="inline mr-2" />
                  Add Field
                </Button>
              </div>

              <div className="space-y-2">
                {displayFields.map((field: any) => {
                  const iconEntry = FIELD_ICONS.find((i) => i.name === field.options?.icon);
                  const FieldIconComp = iconEntry?.Icon;
                  return (
                    <div
                      key={field.id}
                      draggable
                      onDragStart={(e) => handleFieldDragStart(e, field.id)}
                      onDragOver={handleFieldDragOver}
                      onDrop={(e) => handleFieldDrop(e, field.id)}
                      onDragEnd={handleFieldDragEnd}
                      onClick={() => openFieldModal(field)}
                      className={`flex items-center space-x-3 p-3 bg-gray-50 rounded-lg group transition-opacity cursor-pointer hover:bg-gray-100 ${
                        draggedFieldId === field.id ? "opacity-50" : ""
                      }`}
                    >
                      <FaGripVertical
                        className="text-gray-300 cursor-grab"
                        onClick={(e) => e.stopPropagation()}
                      />
                      {FieldIconComp && <FieldIconComp className="w-4 h-4 text-gray-400" />}
                      <span className="flex-1 font-medium">{field.label}</span>
                      {field.options?.currency && (
                        <span className="text-xs text-gray-400">{field.options.currency}</span>
                      )}
                      {field.options?.hideFromCard && (
                        <span className="text-xs text-gray-400 italic">hidden</span>
                      )}
                      <span className="text-xs text-gray-400 bg-gray-200 px-2 py-1 rounded">
                        {FIELD_TYPES.find((t) => t.value === field.type)?.label || field.type}
                      </span>
                      {field.required && (
                        <span className="text-xs text-red-500">Required</span>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDeleteField(field.id); }}
                        className="text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  );
                })}

                {displayFields.length === 0 && (
                  <p className="text-center text-gray-400 py-8">
                    No custom fields yet. Add fields to capture additional data for each item.
                  </p>
                )}
              </div>
            </div>
          )}

          {activeTab === "automations" && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <p className="text-sm text-gray-500">
                  Create automations to automatically create linked cards when conditions are met.
                </p>
                <Button onClick={() => openAutomationModal()}>
                  <FaPlus className="inline mr-2" />
                  Add Automation
                </Button>
              </div>

              {/* Legend for automation types */}
              {automations.some((a: any) => a.relationship === "source") &&
                automations.some((a: any) => a.relationship === "target") && (
                  <div className="mb-4 flex items-center gap-4 text-xs">
                    <div className="flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-green-400"></span>
                      <span className="text-gray-600">Outgoing (triggers from this board)</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-blue-400"></span>
                      <span className="text-gray-600">Incoming (creates cards here)</span>
                    </div>
                  </div>
                )}

              <div className="space-y-3">
                {automations.map((automation: any) => (
                  <div
                    key={automation.id}
                    className={`flex items-center justify-between p-4 rounded-lg group ${
                      automation.relationship === "target" ? "bg-blue-50 border border-blue-100" : "bg-gray-50"
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => handleToggleAutomation(automation)}
                        className="text-xl"
                        title={automation.enabled ? "Disable" : "Enable"}
                      >
                        {automation.enabled ? (
                          <FaToggleOn className="text-green-500" />
                        ) : (
                          <FaToggleOff className="text-gray-400" />
                        )}
                      </button>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{automation.name}</span>
                          {automation.relationship === "target" && (
                            <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">
                              Incoming
                            </span>
                          )}
                          {automation.relationship === "source" && (
                            <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded">
                              Outgoing
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500">
                          {automation.relationship === "target" ? (
                            <>
                              From {automation.sourceBoard?.name || "Unknown"} → {automation.targetStage?.name}
                            </>
                          ) : (
                            <>
                              Creates cards in {automation.targetBoard?.name} → {automation.targetStage?.name}
                            </>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100">
                      <button
                        onClick={() => openAutomationModal(automation)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <FaEdit />
                      </button>
                      <button
                        onClick={() => handleDeleteAutomation(automation.id)}
                        className="text-gray-400 hover:text-red-500"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                ))}

                {automations.length === 0 && (
                  <p className="text-center text-gray-400 py-8">
                    No automations linked to this board yet.
                  </p>
                )}
              </div>
            </div>
          )}

          {activeTab === "computations" && (
            <div className="space-y-8">
              <div>
                <h3 className="text-lg font-semibold mb-1">Stage Computations</h3>
                <p className="text-sm text-gray-500 mb-4">
                  Define computed values per stage. Formulas can use field names (e.g. <code className="bg-gray-100 px-1 rounded">{"${Deal Value}"}</code>) to aggregate values.
                  Available variables: <code className="bg-gray-100 px-1 rounded">cardCount</code>, <code className="bg-gray-100 px-1 rounded">{"${FieldName}"}</code> (sum), <code className="bg-gray-100 px-1 rounded">{"${avg_FieldName}"}</code>, <code className="bg-gray-100 px-1 rounded">{"${count_FieldName}"}</code>.
                </p>
                <div className="space-y-6">
                  {board.stages?.map((stage: any) => {
                    const fields: any[] = stageComputedFields[stage.id] || [];
                    const setFields = (newFields: any[]) => {
                      setStageComputedFields((prev) => ({ ...prev, [stage.id]: newFields }));
                    };
                    const addField = () => {
                      setFields([...fields, { id: `cf_${Date.now()}`, label: "", formula: "", resultType: "NUMBER" }]);
                    };
                    return (
                      <div key={stage.id} className="border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <span
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: stage.color || "#9CA3AF" }}
                            />
                            <span className="font-medium">{stage.name}</span>
                          </div>
                          <Button sizeVariant="small" onClick={addField}>
                            <FaPlus className="inline mr-1" /> Add computation
                          </Button>
                        </div>
                        {fields.length === 0 && (
                          <p className="text-sm text-gray-400 italic">No computations defined for this stage.</p>
                        )}
                        <div className="space-y-3">
                          {fields.map((cf: any, idx: number) => (
                            <div key={cf.id} className="flex gap-2 items-start bg-gray-50 rounded p-2">
                              <div className="flex-1 grid grid-cols-3 gap-2">
                                <div>
                                  <label className="text-xs text-gray-500 mb-1 block">Label</label>
                                  <input
                                    className="w-full border rounded px-2 py-1 text-sm"
                                    placeholder="e.g., Total Value"
                                    value={cf.label}
                                    onChange={(e) => {
                                      const updated = [...fields];
                                      updated[idx] = { ...cf, label: e.target.value };
                                      setFields(updated);
                                    }}
                                  />
                                </div>
                                <div className="col-span-1">
                                  <div className="flex items-center justify-between mb-1">
                                    <label className="text-xs text-gray-500">Formula</label>
                                    <button type="button" onClick={() => setShowFormulaHelp(true)} className="text-primary" title="Formula reference">
                                      <InformationCircleIcon className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                  <FormulaInput
                                    value={cf.formula}
                                    onChange={(val) => {
                                      const updated = [...fields];
                                      updated[idx] = { ...cf, formula: val };
                                      setFields(updated);
                                    }}
                                    fields={board?.fieldDefinitions ?? []}
                                    placeholder="e.g., ${Deal Value}"
                                    rows={1}
                                  />
                                </div>
                                <div>
                                  <label className="text-xs text-gray-500 mb-1 block">Type</label>
                                  <select
                                    className="w-full border rounded px-2 py-1 text-sm"
                                    value={cf.resultType}
                                    onChange={(e) => {
                                      const updated = [...fields];
                                      updated[idx] = { ...cf, resultType: e.target.value };
                                      setFields(updated);
                                    }}
                                  >
                                    <option value="NUMBER">Number</option>
                                    <option value="STRING">String</option>
                                  </select>
                                </div>
                                <div>
                                  <label className="text-xs text-gray-500 mb-1 block">Icon</label>
                                  <select
                                    className="w-full border rounded px-2 py-1 text-sm"
                                    value={cf.icon || ""}
                                    onChange={(e) => {
                                      const updated = [...fields];
                                      updated[idx] = { ...cf, icon: e.target.value || undefined };
                                      setFields(updated);
                                    }}
                                  >
                                    <option value="">No icon</option>
                                    {FIELD_ICONS.map(({ name }) => (
                                      <option key={name} value={name}>{name.replace(/Icon$/, "")}</option>
                                    ))}
                                  </select>
                                </div>
                                {cf.resultType === "NUMBER" && (
                                  <div>
                                    <label className="text-xs text-gray-500 mb-1 block">Currency</label>
                                    <input
                                      className="w-full border rounded px-2 py-1 text-sm"
                                      placeholder="e.g. € or $ or CHF"
                                      value={cf.currency || ""}
                                      onChange={(e) => {
                                        const updated = [...fields];
                                        updated[idx] = { ...cf, currency: e.target.value || undefined };
                                        setFields(updated);
                                      }}
                                    />
                                  </div>
                                )}
                              </div>
                              <button
                                className="text-gray-400 hover:text-red-500 mt-5"
                                onClick={() => setFields(fields.filter((_: any, i: number) => i !== idx))}
                              >
                                <FaTrash />
                              </button>
                            </div>
                          ))}
                        </div>
                        {fields.length > 0 && (
                          <div className="mt-3 flex justify-end">
                            <Button sizeVariant="small" onClick={() => handleSaveStageComputedFields(stage.id, fields)}>
                              <FaSave className="inline mr-1" /> Save stage computations
                            </Button>
                          </div>
                        )}
                        {fields.length === 0 && (stageComputedFields[stage.id]?.length ?? 0) === 0 ? null : (
                          fields.length === 0 ? (
                            <div className="mt-3 flex justify-end">
                              <Button sizeVariant="small" onClick={() => handleSaveStageComputedFields(stage.id, [])}>
                                <FaSave className="inline mr-1" /> Save (clear)
                              </Button>
                            </div>
                          ) : null
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold mb-1">Board Computations</h3>
                <p className="text-sm text-gray-500 mb-4">
                  Define computed values for the whole board. Access stage values via <code className="bg-gray-100 px-1 rounded">StageName_FieldName</code> (e.g. <code className="bg-gray-100 px-1 rounded">Qualified_Deal_Value</code>).
                  Variables: <code className="bg-gray-100 px-1 rounded">cardCount</code>, <code className="bg-gray-100 px-1 rounded">stageCount</code>, <code className="bg-gray-100 px-1 rounded">{"${FieldName}"}</code> (board-wide sum), <code className="bg-gray-100 px-1 rounded">StageName_cardCount</code>.
                </p>
                <div className="space-y-3">
                  {boardComputedFieldDefs.map((cf: any, idx: number) => (
                    <div key={cf.id} className="flex gap-2 items-start bg-gray-50 rounded p-2">
                      <div className="flex-1 grid grid-cols-3 gap-2">
                        <div>
                          <label className="text-xs text-gray-500 mb-1 block">Label</label>
                          <input
                            className="w-full border rounded px-2 py-1 text-sm"
                            placeholder="e.g., Total Deals"
                            value={cf.label}
                            onChange={(e) => {
                              const updated = [...boardComputedFieldDefs];
                              updated[idx] = { ...cf, label: e.target.value };
                              setBoardComputedFieldDefs(updated);
                            }}
                          />
                        </div>
                        <div>
                          <div className="flex items-center justify-between mb-1">
                            <label className="text-xs text-gray-500">Formula</label>
                            <button type="button" onClick={() => setShowFormulaHelp(true)} className="text-primary" title="Formula reference">
                              <InformationCircleIcon className="w-3.5 h-3.5" />
                            </button>
                          </div>
                          <FormulaInput
                            value={cf.formula}
                            onChange={(val) => {
                              const updated = [...boardComputedFieldDefs];
                              updated[idx] = { ...cf, formula: val };
                              setBoardComputedFieldDefs(updated);
                            }}
                            fields={board?.fieldDefinitions ?? []}
                            placeholder="e.g., cardCount or ${Deal Value}"
                            rows={1}
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-500 mb-1 block">Type</label>
                          <select
                            className="w-full border rounded px-2 py-1 text-sm"
                            value={cf.resultType}
                            onChange={(e) => {
                              const updated = [...boardComputedFieldDefs];
                              updated[idx] = { ...cf, resultType: e.target.value };
                              setBoardComputedFieldDefs(updated);
                            }}
                          >
                            <option value="NUMBER">Number</option>
                            <option value="STRING">String</option>
                          </select>
                        </div>
                        <div>
                          <label className="text-xs text-gray-500 mb-1 block">Icon</label>
                          <select
                            className="w-full border rounded px-2 py-1 text-sm"
                            value={cf.icon || ""}
                            onChange={(e) => {
                              const updated = [...boardComputedFieldDefs];
                              updated[idx] = { ...cf, icon: e.target.value || undefined };
                              setBoardComputedFieldDefs(updated);
                            }}
                          >
                            <option value="">No icon</option>
                            {FIELD_ICONS.map(({ name }) => (
                              <option key={name} value={name}>{name.replace(/Icon$/, "")}</option>
                            ))}
                          </select>
                        </div>
                        {cf.resultType === "NUMBER" && (
                          <div>
                            <label className="text-xs text-gray-500 mb-1 block">Currency</label>
                            <input
                              className="w-full border rounded px-2 py-1 text-sm"
                              placeholder="e.g. € or $ or CHF"
                              value={cf.currency || ""}
                              onChange={(e) => {
                                const updated = [...boardComputedFieldDefs];
                                updated[idx] = { ...cf, currency: e.target.value || undefined };
                                setBoardComputedFieldDefs(updated);
                              }}
                            />
                          </div>
                        )}
                      </div>
                      <button
                        className="text-gray-400 hover:text-red-500 mt-5"
                        onClick={() => setBoardComputedFieldDefs(boardComputedFieldDefs.filter((_: any, i: number) => i !== idx))}
                      >
                        <FaTrash />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between mt-4">
                  <Button
                    variant="outlined"
                    onClick={() => setBoardComputedFieldDefs([
                      ...boardComputedFieldDefs,
                      { id: `bcf_${Date.now()}`, label: "", formula: "", resultType: "NUMBER" },
                    ])}
                  >
                    <FaPlus className="inline mr-2" /> Add board computation
                  </Button>
                  <Button onClick={handleSaveBoardComputedFields}>
                    <FaSave className="inline mr-2" /> Save board computations
                  </Button>
                </div>
              </div>
            </div>
          )}

          {activeTab === "cardLayout" && (
            <CardLayoutEditor
              cardLayout={cardLayoutConfig}
              onChange={setCardLayoutConfig}
              fieldDefinitions={board?.fieldDefinitions || []}
              onSave={handleSaveCardLayout}
            />
          )}
        </div>
      </div>

      {showStageModal && (
        <Modal
          title={editingStage ? "Edit Stage" : "Add Stage"}
          closeFunction={() => setShowStageModal(false)}
          autoHeight
        >
          <div className="space-y-4">
            <FormInput
              label="Stage Name"
              value={stageName}
              onChange={(e) => setStageName(e.target.value)}
              placeholder="e.g., Lead, Qualified, Proposal"
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
              <input
                type="color"
                value={stageColor}
                onChange={(e) => setStageColor(e.target.value)}
                className="w-full h-10 rounded cursor-pointer"
              />
            </div>
            <div className="flex justify-end space-x-2 pt-4">
              <Button variant="outlined" onClick={() => setShowStageModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveStage}>
                {editingStage ? "Save" : "Add Stage"}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {showFieldModal && (
        <Modal
          title={editingField ? "Edit Field" : "Add Field"}
          closeFunction={() => setShowFieldModal(false)}
          autoHeight
        >
          <div className="space-y-4">
            <FormInput
              label="Field Label"
              value={fieldLabel}
              onChange={(e) => setFieldLabel(e.target.value)}
              placeholder="e.g., Company Name, Deal Value"
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Field Type</label>
              <select
                className="w-full border rounded px-3 py-2"
                value={fieldType}
                onChange={(e) => setFieldType(e.target.value)}
              >
                {FIELD_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            {["SELECT", "MULTI_SELECT"].includes(fieldType) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Options</label>
                <div className="space-y-2">
                  {fieldOptions.map((opt, index) => (
                    <div key={index} className="flex space-x-2">
                      <input
                        type="text"
                        className="flex-1 border rounded px-2 py-1 text-sm"
                        placeholder="Label"
                        value={opt.label}
                        onChange={(e) => updateFieldOption(index, "label", e.target.value)}
                      />
                      <input
                        type="text"
                        className="w-32 border rounded px-2 py-1 text-sm"
                        placeholder="Value"
                        value={opt.value}
                        onChange={(e) => updateFieldOption(index, "value", e.target.value)}
                      />
                      <button
                        onClick={() => removeFieldOption(index)}
                        className="text-red-500 hover:text-red-700 px-2"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={addFieldOption}
                    className="text-sm text-primary hover:underline"
                  >
                    + Add option
                  </button>
                </div>
              </div>
            )}

            {fieldType === "COMPUTED" && (
              <div className="space-y-3">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-gray-700">Formula</label>
                    <button
                      type="button"
                      onClick={() => setShowFormulaHelp(true)}
                      className="flex items-center gap-1 text-xs text-primary hover:underline"
                    >
                      <InformationCircleIcon className="w-4 h-4" />
                      Formula reference
                    </button>
                  </div>
                  <FormulaInput
                    value={fieldFormula}
                    onChange={setFieldFormula}
                    fields={board?.fieldDefinitions?.filter((f: any) => f.type !== "COMPUTED" || f.id !== editingField?.id) ?? []}
                    placeholder="e.g., ${Price} * ${Quantity}"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Type <code className="bg-gray-100 px-0.5 rounded">{'${'}</code> to autocomplete field names.
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Result Type</label>
                  <select
                    className="w-full border rounded px-3 py-2"
                    value={fieldResultType}
                    onChange={(e) => setFieldResultType(e.target.value as "STRING" | "NUMBER")}
                  >
                    <option value="STRING">Text</option>
                    <option value="NUMBER">Number</option>
                  </select>
                </div>
              </div>
            )}

            {fieldType === "CARD" && (
              <div className="p-3 bg-blue-50 rounded text-sm text-blue-700">
                Card fields allow linking to other items. They can be populated manually or automatically via automations.
              </div>
            )}

            {/* Icon picker */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Icon (optional)</label>
              <div className="flex flex-wrap gap-2 p-2 border rounded bg-gray-50 max-h-36 overflow-y-auto">
                <button
                  type="button"
                  onClick={() => setFieldIcon("")}
                  className={`p-1.5 rounded text-xs text-gray-500 border ${
                    fieldIcon === "" ? "border-primary bg-primary/10" : "border-transparent hover:border-gray-300"
                  }`}
                  title="No icon"
                >
                  None
                </button>
                {FIELD_ICONS.map(({ name, Icon }) => (
                  <button
                    key={name}
                    type="button"
                    onClick={() => setFieldIcon(name)}
                    className={`p-1.5 rounded border ${
                      fieldIcon === name ? "border-primary bg-primary/10 text-primary" : "border-transparent hover:border-gray-300 text-gray-600"
                    }`}
                    title={name.replace(/Icon$/, "")}
                  >
                    <Icon className="w-4 h-4" />
                  </button>
                ))}
              </div>
            </div>

            {/* Currency (number fields only) */}
            {fieldType === "NUMBER" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Currency symbol (optional)</label>
                <input
                  type="text"
                  className="w-full border rounded px-3 py-2"
                  placeholder="e.g. € or $ or CHF"
                  value={fieldCurrency}
                  onChange={(e) => setFieldCurrency(e.target.value)}
                />
              </div>
            )}

            <div className="flex items-center gap-6">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={fieldRequired}
                  onChange={(e) => setFieldRequired(e.target.checked)}
                  className="w-4 h-4"
                />
                <span className="text-sm">Required field</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={fieldHideFromCard}
                  onChange={(e) => setFieldHideFromCard(e.target.checked)}
                  className="w-4 h-4"
                />
                <span className="text-sm">Hide from card overview</span>
              </label>
            </div>

            <div className="flex justify-end space-x-2 pt-4">
              <Button variant="outlined" onClick={() => setShowFieldModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveField}>
                {editingField ? "Save" : "Add Field"}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {showAutomationModal && (
        <Modal
          title={editingAutomation ? "Edit Automation" : "Add Automation"}
          closeFunction={() => setShowAutomationModal(false)}
          autoHeight
        >
          <div className="space-y-4 max-h-[70vh] overflow-y-auto">
            <FormInput
              label="Automation Name"
              value={automationName}
              onChange={(e) => setAutomationName(e.target.value)}
              placeholder="e.g., Create follow-up task when deal closes"
            />

            {/* Trigger Conditions Section */}
            <div className="border rounded-lg p-4 bg-gray-50">
              <h4 className="font-medium text-sm mb-3">Trigger Conditions</h4>
              
              <div className="space-y-3">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    When card moves to stage (optional)
                  </label>
                  <select
                    className="w-full border rounded px-3 py-2 bg-white"
                    value={automationTriggerStageId}
                    onChange={(e) => setAutomationTriggerStageId(e.target.value)}
                  >
                    <option value="">Any stage / No stage trigger</option>
                    {board.stages?.map((stage: any) => (
                      <option key={stage.id} value={stage.id}>
                        {stage.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    Additional field conditions
                  </label>
                  {automationTriggerConditions.map((cond, idx) => (
                    <div key={idx} className="flex gap-2 mb-2">
                      <select
                        className="flex-1 border rounded px-2 py-1 text-sm bg-white"
                        value={cond.fieldId}
                        onChange={(e) => {
                          const newConds = [...automationTriggerConditions];
                          newConds[idx].fieldId = e.target.value;
                          setAutomationTriggerConditions(newConds);
                        }}
                      >
                        <option value="">Select field...</option>
                        {board.fieldDefinitions?.map((f: any) => (
                          <option key={f.id} value={f.id}>{f.label}</option>
                        ))}
                      </select>
                      <select
                        className="w-24 border rounded px-2 py-1 text-sm bg-white"
                        value={cond.operator}
                        onChange={(e) => {
                          const newConds = [...automationTriggerConditions];
                          newConds[idx].operator = e.target.value;
                          setAutomationTriggerConditions(newConds);
                        }}
                      >
                        <option value="equals">=</option>
                        <option value="not_equals">≠</option>
                        <option value="contains">contains</option>
                        <option value="gt">&gt;</option>
                        <option value="lt">&lt;</option>
                      </select>
                      <input
                        type="text"
                        className="flex-1 border rounded px-2 py-1 text-sm"
                        value={cond.value}
                        onChange={(e) => {
                          const newConds = [...automationTriggerConditions];
                          newConds[idx].value = e.target.value;
                          setAutomationTriggerConditions(newConds);
                        }}
                        placeholder="Value"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setAutomationTriggerConditions(
                            automationTriggerConditions.filter((_, i) => i !== idx)
                          );
                        }}
                        className="text-red-500 hover:text-red-700 px-2"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      setAutomationTriggerConditions([
                        ...automationTriggerConditions,
                        { fieldId: "", operator: "equals", value: "" },
                      ]);
                    }}
                    className="text-sm text-primary hover:underline"
                  >
                    + Add field condition
                  </button>
                </div>
              </div>
            </div>

            {/* Target Section */}
            <div className="border rounded-lg p-4">
              <h4 className="font-medium text-sm mb-3">Create New Card</h4>
              
              <div className="space-y-3">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    In board
                  </label>
                  <select
                    className="w-full border rounded px-3 py-2"
                    value={automationTargetBoardId}
                    onChange={(e) => {
                      setAutomationTargetBoardId(e.target.value);
                      setAutomationTargetStageId("");
                      setAutomationFieldMappings([]);
                    }}
                  >
                    <option value="">Select board...</option>
                    {allBoards.map((b: any) => (
                      <option key={b.id} value={b.id}>
                        {b.name}
                      </option>
                    ))}
                  </select>
                </div>

                {automationTargetBoardId && (
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">
                      In stage
                    </label>
                    <select
                      className="w-full border rounded px-3 py-2"
                      value={automationTargetStageId}
                      onChange={(e) => setAutomationTargetStageId(e.target.value)}
                    >
                      <option value="">Select stage...</option>
                      {allBoards
                        .find((b: any) => b.id === automationTargetBoardId)
                        ?.stages?.map((stage: any) => (
                          <option key={stage.id} value={stage.id}>
                            {stage.name}
                          </option>
                        ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    Card title formula
                  </label>
                  <input
                    type="text"
                    className="w-full border rounded px-3 py-2 font-mono text-sm"
                    value={automationTitleFormula}
                    onChange={(e) => setAutomationTitleFormula(e.target.value)}
                    placeholder="${title}"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Use {"${title}"} for source title, {"${fieldLabel}"} for field values
                  </p>
                </div>
              </div>
            </div>

            {/* Field Mappings Section */}
            {automationTargetBoardId && (
              <div className="border rounded-lg p-4">
                <h4 className="font-medium text-sm mb-3">Field Values for New Card</h4>
                <p className="text-xs text-gray-500 mb-3">
                  Set values for fields on the new card using formulas from the source card
                </p>
                
                <div className="space-y-2">
                  {automationFieldMappings.map((mapping, idx) => {
                    const targetBoard = allBoards.find((b: any) => b.id === automationTargetBoardId);
                    return (
                      <div key={idx} className="flex gap-2 items-start">
                        <select
                          className="w-1/3 border rounded px-2 py-1 text-sm"
                          value={mapping.targetFieldId}
                          onChange={(e) => {
                            const newMappings = [...automationFieldMappings];
                            newMappings[idx].targetFieldId = e.target.value;
                            setAutomationFieldMappings(newMappings);
                          }}
                        >
                          <option value="">Target field...</option>
                          {targetBoard?.fieldDefinitions?.map((f: any) => (
                            <option key={f.id} value={f.id}>{f.label}</option>
                          ))}
                        </select>
                        <span className="py-1">=</span>
                        <input
                          type="text"
                          className="flex-1 border rounded px-2 py-1 text-sm font-mono"
                          value={mapping.formula}
                          onChange={(e) => {
                            const newMappings = [...automationFieldMappings];
                            newMappings[idx].formula = e.target.value;
                            setAutomationFieldMappings(newMappings);
                          }}
                          placeholder="${sourceFieldLabel}"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setAutomationFieldMappings(
                              automationFieldMappings.filter((_, i) => i !== idx)
                            );
                          }}
                          className="text-red-500 hover:text-red-700 px-2 py-1"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    );
                  })}
                  <button
                    type="button"
                    onClick={() => {
                      setAutomationFieldMappings([
                        ...automationFieldMappings,
                        { targetFieldId: "", formula: "" },
                      ]);
                    }}
                    className="text-sm text-primary hover:underline"
                  >
                    + Add field mapping
                  </button>
                </div>
                
                <div className="mt-3 p-2 bg-gray-50 rounded text-xs text-gray-600">
                  <strong>Available variables:</strong>{" "}
                  {"${title}"}{board.fieldDefinitions?.length > 0 && ", "}
                  {board.fieldDefinitions?.map((f: any) => `\${${f.label}}`).join(", ")}
                </div>
              </div>
            )}

            {/* User Assignments Section */}
            {automationTargetBoardId && (
              <div className="border rounded-lg p-4">
                <h4 className="font-medium text-sm mb-3">User Assignments for New Card</h4>
                <p className="text-xs text-gray-500 mb-3">
                  Assign users to the new card automatically. Choose a fixed user or copy assignees from the source card.
                </p>

                <div className="space-y-2">
                  {automationUserMappings.map((mapping, idx) => (
                    <div key={idx} className="flex gap-2 items-center">
                      <select
                        className="w-40 border rounded px-2 py-1 text-sm bg-white"
                        value={mapping.type}
                        onChange={(e) => {
                          const newMappings = [...automationUserMappings];
                          newMappings[idx].type = e.target.value as "fixed" | "copy_from_source";
                          newMappings[idx].userId = "";
                          setAutomationUserMappings(newMappings);
                        }}
                      >
                        <option value="fixed">Fixed user</option>
                        <option value="copy_from_source">Copy from source</option>
                      </select>
                      {mapping.type === "fixed" && (
                        <select
                          className="flex-1 border rounded px-2 py-1 text-sm bg-white"
                          value={mapping.userId}
                          onChange={(e) => {
                            const newMappings = [...automationUserMappings];
                            newMappings[idx].userId = e.target.value;
                            setAutomationUserMappings(newMappings);
                          }}
                        >
                          <option value="">Select user...</option>
                          {allUsers.map((u: any) => (
                            <option key={u.id} value={u.id}>{u.name}</option>
                          ))}
                        </select>
                      )}
                      {mapping.type === "copy_from_source" && (
                        <span className="text-xs text-gray-500 flex-1">
                          All assignees from the source card will be copied
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          setAutomationUserMappings(
                            automationUserMappings.filter((_, i) => i !== idx)
                          );
                        }}
                        className="text-red-500 hover:text-red-700 px-2 py-1"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      setAutomationUserMappings([
                        ...automationUserMappings,
                        { type: "fixed", userId: "" },
                      ]);
                    }}
                    className="text-sm text-primary hover:underline"
                  >
                    + Add user assignment
                  </button>
                </div>
              </div>
            )}

            {/* Parent Link Section */}
            {automationTargetBoardId && (
              <div className="border rounded-lg p-4">
                <h4 className="font-medium text-sm mb-3">Link Back to Source Card</h4>
                <p className="text-xs text-gray-500 mb-3">
                  Select a CARD field on the new card to store a link to the source card (optional)
                </p>
                <select
                  className="w-full border rounded px-3 py-2"
                  value={automationParentLinkFieldId}
                  onChange={(e) => setAutomationParentLinkFieldId(e.target.value)}
                >
                  <option value="">No link (no CARD field selected)</option>
                  {allBoards
                    .find((b: any) => b.id === automationTargetBoardId)
                    ?.fieldDefinitions?.filter((f: any) => f.type === "CARD")
                    ?.map((field: any) => (
                      <option key={field.id} value={field.id}>
                        {field.label}
                      </option>
                    ))}
                </select>
              </div>
            )}

            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={automationAllowMultiple}
                onChange={(e) => setAutomationAllowMultiple(e.target.checked)}
                className="w-4 h-4"
              />
              <span className="text-sm">Allow triggering multiple times per card</span>
            </label>

            <div className="flex justify-end space-x-2 pt-4">
              <Button variant="outlined" onClick={() => setShowAutomationModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveAutomation}>
                {editingAutomation ? "Save" : "Add Automation"}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {showFormulaHelp && (
        <FormulaHelpModal onClose={() => setShowFormulaHelp(false)} />
      )}
    </div>
  );
}
